import React, { useState } from 'react';
import {
  Button,
  ButtonGroup,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import {
  Download as DownloadIcon,
  PictureAsPdf as PdfIcon,
  TableChart as CsvIcon,
  Description as DescriptionIcon,
  Note as NoteIcon,
  ArrowDropDown as ArrowDropDownIcon,
} from '@mui/icons-material';
import { useTranslation } from '../../i18n';
import { exportService, ExportType } from '../../services/exportService';

interface ExportButtonProps {
  courseId?: number;
  variant?: 'contained' | 'outlined' | 'text';
  size?: 'small' | 'medium' | 'large';
  color?: 'primary' | 'secondary' | 'inherit';
  showLabel?: boolean;
}

const ExportButton: React.FC<ExportButtonProps> = ({
  courseId,
  variant = 'outlined',
  size = 'medium',
  color = 'primary',
  showLabel = true,
}) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [loading, setLoading] = useState<ExportType | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleExport = async (type: ExportType) => {
    setLoading(type);
    setError(null);
    handleClose();

    try {
      switch (type) {
        case 'learning-history':
          await exportService.downloadLearningHistory();
          break;
        case 'transcript':
          if (courseId) {
            await exportService.downloadTranscript(courseId);
          }
          break;
        case 'notes':
          await exportService.downloadNotes(courseId);
          break;
        case 'progress-report':
          await exportService.downloadProgressReport();
          break;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed');
      console.error('Export error:', err);
    } finally {
      setLoading(null);
    }
  };

  const getIcon = (type: ExportType) => {
    switch (type) {
      case 'learning-history':
        return <PdfIcon fontSize="small" />;
      case 'transcript':
        return <DescriptionIcon fontSize="small" />;
      case 'notes':
        return <NoteIcon fontSize="small" />;
      case 'progress-report':
        return <CsvIcon fontSize="small" />;
      default:
        return <DownloadIcon fontSize="small" />;
    }
  };

  const exportOptions = courseId
    ? exportService.getCourseExportOptions(courseId)
    : exportService.getExportOptions();

  return (
    <>
      <ButtonGroup variant={variant} size={size} color={color}>
        <Button
          onClick={handleClick}
          startIcon={loading ? <CircularProgress size={16} /> : <DownloadIcon />}
          endIcon={<ArrowDropDownIcon />}
          disabled={!!loading}
        >
          {showLabel && t('export.button', 'Export')}
        </Button>
      </ButtonGroup>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {exportOptions.map((option) => (
          <MenuItem
            key={option.type}
            onClick={() => handleExport(option.type)}
            disabled={loading === option.type}
          >
            <ListItemIcon>
              {loading === option.type ? (
                <CircularProgress size={20} />
              ) : (
                getIcon(option.type)
              )}
            </ListItemIcon>
            <ListItemText
              primary={t(`export.${option.type}.label`, option.label)}
              secondary={t(`export.${option.type}.description`, option.description)}
            />
          </MenuItem>
        ))}
      </Menu>

      {error && (
        <div style={{ color: 'red', fontSize: '0.75rem', marginTop: '4px' }}>
          {error}
        </div>
      )}
    </>
  );
};

export default ExportButton;
